import * as Yup from "yup"

import type { AppLocalization } from "../../../../../modules/localization/appLocalization"
import { DateTimeFactory } from "../../../../../modules/localization/useDateTime"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { PaymentMethodType } from "../../../../../modules/payments/paymentTypes"

import {
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    CreateOrderDialogType,
    MeetingServicePriceListStatus,
    SelectedMainUserState,
} from "../../createOrderTypes"
import { createInitEvents } from "../resource/resourceBookingCalendars/helpers"
import { buildMultipleDeliveriesState } from "./buildMultipleDeliveriesState"
import {
    CustomFieldsGroup,
    DeliveryTimesField,
    MeetingServiceConfig,
    DynamicConfigKey,
    DynamicConfigField,
    ExtendedDynamicConfigKey,
    BookingConfig,
    DeliveryFulfillmentType,
    ValidationType,
} from "./formTypes"
import {
    buildFieldSchema,
    defaultPaymentMethod,
    getBookingResourceWithTableSetup,
    INVALID_DELIVERIES,
    isAccountingDimensionKey,
} from "./helpers"
import { mapConfigFieldsToValues } from "./mapConfigFieldsToValues"
import { defaultMultipleDeliveries } from "../../defaults"
import { getCreateOrderDialogTypeFlags } from "../../hooks/useCreateOrderDialogType"

export const DELIVERY_LOCATION_HAS_OPTIONS_FIELD = "__deliveryLocationHasOptions"

export const getAllConfigFields = (config?: MeetingServiceConfig | BookingConfig): DynamicConfigField[] => {
    if (!config?.sections?.length) return []

    return config.sections
        .filter((section) => section.showInDrawer !== false)
        .flatMap(({ groups }) => groups ?? [])
        .flatMap(({ fields }) => fields ?? [])
}

function ensurePaymentMethodValue(
    values: Partial<Record<ExtendedDynamicConfigKey, unknown>>,
    orderDetails?: MeetingServiceOrder,
): Partial<Record<ExtendedDynamicConfigKey, unknown>> {
    const raw = values[DynamicConfigKey.PAYMENT_METHOD]
    const rawValue = raw && typeof raw === "object" && "value" in raw ? (raw as { value?: unknown }).value : undefined

    if (rawValue === PaymentMethodType.INVOICE || rawValue === PaymentMethodType.CASH) {
        return values
    }

    const fromApi = orderDetails?.paymentDetails?.method

    return {
        ...values,
        [DynamicConfigKey.PAYMENT_METHOD]: fromApi ? { value: fromApi } : defaultPaymentMethod,
    }
}

export function getValuesFromConfig(
    user: SelectedMainUserState,
    dateTimeFactory: DateTimeFactory,
    dialogType: CreateOrderDialogType,
    config?: MeetingServiceConfig | BookingConfig,
    orderDetails?: MeetingServiceOrder,
    mode?: CreateOrderDialogMode,
    flowConfig?: CreateOrderDialogFlow,
) {
    const values = mapConfigFieldsToValues({
        fields: getAllConfigFields(config),
        user,
        dialogType,
        orderDetails,
        dateTimeFactory,
        flowConfig,
        mode,
    })

    // Always have PAYMENT_METHOD in Formik values so UI guards, validation, and payload behave
    // as if API had sent a payment method (INVOICE by default).
    return ensurePaymentMethodValue(values, orderDetails)
}

export function getCustomFieldsValues(
    customFields: CustomFieldsGroup,
    dateTimeFactory: DateTimeFactory,
    dialogType: CreateOrderDialogType,
    orderDetails?: MeetingServiceOrder,
    mode?: CreateOrderDialogMode,
    flowConfig?: CreateOrderDialogFlow,
) {
    let result = { ...customFields }

    // Always keep MULTIPLE_DELIVERIES in form values so validation/UI are stable across configs and flows.
    if (!result[DynamicConfigKey.MULTIPLE_DELIVERIES]) {
        result[DynamicConfigKey.MULTIPLE_DELIVERIES] = defaultMultipleDeliveries
    }

    if (orderDetails) {
        const { webshop, merchant, priceList, bookingDetails, meetingDetails, deliveries, amount, created, updated } =
            orderDetails
        const start = dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(meetingDetails.startDateTime))
        const end = dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(meetingDetails.endDateTime))

        if (mode === "edit" && bookingDetails && bookingDetails.resources && bookingDetails.resources.length > 0) {
            const resourceIds = bookingDetails.resources.map((resource) => resource.id.toString())

            if (resourceIds.length > 0 && start && end) {
                result.event = createInitEvents(resourceIds, start, end)
            }

            result.fullResourcesList = bookingDetails.resources.map((resource) => ({
                title: resource.name,
                id: resource.id,
                type: {
                    id: resource.resourceTypeId,
                    name: resource.resourceType,
                },
                ...(resource.room?.tableSetup
                    ? {
                          tableSetups: [
                              {
                                  key: resource.room.tableSetup.key,
                                  name: resource.room.tableSetup.name,
                                  imageUrl: resource.room.tableSetup.imageUrl,
                              },
                          ],
                      }
                    : {}),
            }))
            result.resources = getBookingResourceWithTableSetup(bookingDetails.resources)
        } else if (flowConfig && start && end) {
            result.event = createInitEvents([], start, end)
        }

        if (webshop && merchant && priceList) {
            const priceListValue = {
                merchantId: merchant.id,
                merchantName: merchant.name,
                priceListId: priceList.id,
                priceListName: priceList?.name || "",
                shopId: webshop.id,
                isOnlyOnePriceList: false,
                priceListIncludeVat: priceList.includeVat,
            }
            result.priceList = { item: priceListValue, status: MeetingServicePriceListStatus.VALIDATING }
            result.initPriceList = priceListValue
        }

        if (deliveries) {
            const md = buildMultipleDeliveriesState({
                mode,
                dialogType,
                flowConfig,
                orderDetails,
                dateTimeFactory,
            })
            if (md) {
                result[DynamicConfigKey.MULTIPLE_DELIVERIES] = md
            }
        }
        if (mode === "create" && flowConfig && orderDetails) {
            if (start && end) {
                result.event = createInitEvents([], start, end)
            }
        }
        if (amount) result.initAmount = amount
        if (created) result.created = created
        if (amount) result.updated = updated
    }

    return result
}

// Makes the field optional if PAYMENT_METHOD.value !== “INVOICE”
const relaxUnlessInvoice = (sch: Yup.Schema<any>) =>
    sch.when(`${DynamicConfigKey.PAYMENT_METHOD}.value`, (pmValue: unknown, s: Yup.Schema<any>) => {
        const tag = pmValue == null ? undefined : String(pmValue).toUpperCase()
        return tag === "INVOICE" ? s : s.notRequired().nullable()
    })

export function generateValidationSchema(
    dialogType: CreateOrderDialogType,
    l10n: AppLocalization,
    meetingServiceConfiguration?: MeetingServiceConfig,
): Yup.ObjectSchema<Record<ExtendedDynamicConfigKey, any>> {
    const shape: Partial<Record<ExtendedDynamicConfigKey, Yup.Schema<any>>> = {}
    const { isSpecialDialog } = getCreateOrderDialogTypeFlags(dialogType)

    const allFields: DynamicConfigField[] = meetingServiceConfiguration
        ? getAllConfigFields(meetingServiceConfiguration).filter(Boolean)
        : []

    const activeFieldKeys = new Set(allFields.filter(({ active }) => active).map(({ key }) => key))

    if (!isSpecialDialog) {
        allFields.forEach((config) => {
            const field = config.inputField
            const key = config.key
            const label = config.label || config.key

            if (
                key === DynamicConfigKey.DELIVERY_LOCATIONS ||
                key === DynamicConfigKey.OTHER_DELIVERY_LOCATION ||
                key === DynamicConfigKey.MULTIPLE_DELIVERIES ||
                key === DynamicConfigKey.SATELLITES
            )
                return

            if (!activeFieldKeys.has(key)) return
            shape[key] = buildFieldSchema(field, label, l10n)
        })

        // REGISTER_PARTICIPANTS is stored as an array. Yup.required() does not fail for empty arrays,
        // so we must enforce non-empty explicitly when required.
        const participantsConfig = allFields.find((f) => f.key === DynamicConfigKey.REGISTER_PARTICIPANTS)
        if (participantsConfig && activeFieldKeys.has(DynamicConfigKey.REGISTER_PARTICIPANTS)) {
            const label = participantsConfig.label || participantsConfig.key
            const isRequired =
                Boolean(participantsConfig.inputField?.isRequired) ||
                Boolean(participantsConfig.inputField?.validations?.some((v) => v.type === ValidationType.REQUIRED))

            if (isRequired) {
                const msg = l10n.getString("create-order-validation-required", { label })
                shape[DynamicConfigKey.REGISTER_PARTICIPANTS] = Yup.array().min(1, msg).required(msg)
            }
        }

        if (activeFieldKeys.has(DynamicConfigKey.DELIVERY_LOCATIONS)) {
            shape[DynamicConfigKey.DELIVERY_LOCATIONS] = Yup.string().nullable()
        }

        if (activeFieldKeys.has(DynamicConfigKey.OTHER_DELIVERY_LOCATION)) {
            shape[DynamicConfigKey.OTHER_DELIVERY_LOCATION] = Yup.string().nullable()
        }
    }

    // Always require MULTIPLE_DELIVERIES (independent of config.active) for meeting-service orders.
    shape[DynamicConfigKey.MULTIPLE_DELIVERIES] = Yup.mixed<DeliveryTimesField>()
        .test(
            "delivery-block-validation",
            l10n.getString("create-order-multiple-deliveries-date-and-time-required"),
            (value) => {
                if (!value) return false
                const hasDate = !!value.deliveryDate
                const hasAtLeastOneTime = Array.isArray(value.deliveryTimes) && value.deliveryTimes.length > 0
                return hasDate && hasAtLeastOneTime
            },
        )
        .test("delivery-has-products", function (value) {
            if (!value || !Array.isArray(value.deliveryTimes)) return true

            const invalidTimes = value.deliveryTimes
                .filter((delivery) => !delivery.orderLines || delivery.orderLines.length === 0)
                .map((delivery) => delivery.time)

            if (invalidTimes.length === 0) {
                return true
            }

            return this.createError({
                message: `${INVALID_DELIVERIES}${invalidTimes.join(",")}`,
            })
        })

    // if (activeFieldKeys.has(DynamicConfigKey.BUYER_PARTIES) && shape[DynamicConfigKey.BUYER_PARTIES]) {
    //     shape[DynamicConfigKey.BUYER_PARTIES] = relaxUnlessInvoice(shape[DynamicConfigKey.BUYER_PARTIES]!)
    // }

    if (!isSpecialDialog) {
        // 2) All accounting dimension keys → also NOT mandatory if ≠ INVOICE
        allFields
            .filter((f) => activeFieldKeys.has(f.key) && isAccountingDimensionKey(f.key))
            .forEach((f) => {
                const k = f.key as ExtendedDynamicConfigKey
                if (shape[k]) {
                    shape[k] = relaxUnlessInvoice(shape[k]!)
                }
            })
    }

    return Yup.object()
        .shape(shape as Record<ExtendedDynamicConfigKey, Yup.Schema<any>>)
        .test(
            "delivery-location-required",
            l10n.getString("create-order-delivery-location-required"),
            function (value) {
                if (!value || typeof value !== "object") {
                    return true
                }

                // If neither field is present in config, do not block the form.
                const hasAnyDeliveryLocationField =
                    activeFieldKeys.has(DynamicConfigKey.DELIVERY_LOCATIONS) ||
                    activeFieldKeys.has(DynamicConfigKey.OTHER_DELIVERY_LOCATION)

                if (!hasAnyDeliveryLocationField) {
                    return true
                }

                const type = (value as Record<string, unknown>)[DynamicConfigKey.DELIVERY_TYPE]
                const isDelivery = type === DeliveryFulfillmentType.DELIVERY

                if (!isDelivery) {
                    return true
                }

                const hasOptionsRaw = (value as Record<string, unknown>)[DELIVERY_LOCATION_HAS_OPTIONS_FIELD]
                const hasAnyOptions = hasOptionsRaw !== false

                const deliveryLocationId = (value as Record<string, unknown>)[DynamicConfigKey.DELIVERY_LOCATIONS]
                const otherLocation = (value as Record<string, unknown>)[DynamicConfigKey.OTHER_DELIVERY_LOCATION]

                const hasSelectedDeliveryLocation =
                    deliveryLocationId != null && String(deliveryLocationId).trim() !== ""
                const hasOtherLocation = otherLocation != null && String(otherLocation).trim() !== ""

                // If there are no predefined options, the free-text location must be filled.
                if (!hasAnyOptions) {
                    return hasOtherLocation
                        ? true
                        : this.createError({
                              path: DynamicConfigKey.OTHER_DELIVERY_LOCATION,
                              message: l10n.getString("create-order-delivery-location-required"),
                          })
                }

                // Otherwise allow either predefined OR other location.
                return hasSelectedDeliveryLocation || hasOtherLocation
                    ? true
                    : this.createError({
                          path: DynamicConfigKey.DELIVERY_LOCATIONS,
                          message: l10n.getString("create-order-delivery-location-required"),
                      })
            },
        )
}
